// Permission matrix — mirrors backend role groups defined in /app/backend/auth.py
// Each permission key maps to the list of roles allowed to perform the action.

export const ROLES = ["admin", "executive_director", "field_specialist", "care_navigator", "volunteer"];

const ADMIN_ONLY = ["admin"];
const ADMIN_ED = ["admin", "executive_director"];
const STAFF = ["admin", "executive_director", "field_specialist", "care_navigator"];
const ALL_AUTH = ROLES;
const STAFF_PLUS_VOLUNTEER = [...STAFF, "volunteer"];

export const PERMISSIONS = {
  // Users / team
  "users.view": ADMIN_ED,
  "users.create": ADMIN_ONLY,
  "users.edit": ADMIN_ONLY,
  "users.delete": ADMIN_ONLY,

  // Care Seekers
  "care_seekers.view": STAFF,
  "care_seekers.create": STAFF,
  "care_seekers.edit": STAFF,
  "care_seekers.delete": ADMIN_ED,

  // Intakes
  "intakes.create": STAFF,
  "intakes.view": STAFF,

  // Wellness Notes
  "notes.view": STAFF,
  "notes.create": STAFF,
  "notes.edit": STAFF,
  "notes.delete": ADMIN_ED,

  // Referrals
  "referrals.view": STAFF,
  "referrals.create": STAFF,
  "referrals.edit": STAFF,
  "referrals.delete": ADMIN_ED,

  // Resources
  "resources.view": ALL_AUTH,
  "resources.create": STAFF,
  "resources.edit": STAFF,
  "resources.delete": ADMIN_ED,

  // Outreach
  "outreach.view": ALL_AUTH,
  "outreach.create": STAFF_PLUS_VOLUNTEER,
  "outreach.edit": STAFF_PLUS_VOLUNTEER,
  "outreach.delete": ADMIN_ED,

  // Maternal
  "maternal.view": STAFF,
  "maternal.create": STAFF,
  "maternal.edit": STAFF,

  // Tasks
  "tasks.view": ALL_AUTH,
  "tasks.create": STAFF,
  "tasks.edit": ALL_AUTH,
  "tasks.delete": ADMIN_ED,

  // IT Support tickets — every authenticated staff member can log a ticket
  "tickets.view": ALL_AUTH,
  "tickets.create": ALL_AUTH,
  "tickets.edit": ALL_AUTH,  // own + assigned; row-level enforcement via API
  "tickets.delete": ADMIN_ED,

  // Training (knowledge base) — everyone reads; admin/ED manages
  "training.view": ALL_AUTH,
  "training.create": ADMIN_ED,
  "training.edit": ADMIN_ED,
  "training.delete": ADMIN_ED,

  // Appointments
  "appointments.view": STAFF,
  "appointments.create": STAFF,
  "appointments.edit": STAFF,
  "appointments.cancel": STAFF,
  "appointments.delete": ADMIN_ED,

  // Dashboard / Reports
  "dashboard.view": STAFF,
  "reports.view": ADMIN_ED,
  "exports.download": ADMIN_ED,
  // HC-EPIC Monthly Grant Report parity slice — external delivery.
  // Strict permission: `reports.view` alone does NOT admit external
  // sends. Default holders: admin (via backend ALL_PERMS force-sync)
  // + executive_director (via backend additive DEFAULT_ROLES seed).
  "grant_reports.email_external": ADMIN_ED,

  // ----- Dashboards (Iter 77.4 · dashboard-level RBAC) -----
  "dashboard.home.view": STAFF,
  "dashboard.executive_ops.view": ADMIN_ED,
  "dashboard.executive_notifications.view": ADMIN_ED,
  "dashboard.compliance.view": ADMIN_ED,
  "dashboard.reports.view": ADMIN_ED,
  "dashboard.funder.view": ADMIN_ED,

  // Audit
  "audit.view": ADMIN_ONLY,

  // Timesheets — every staff member can manage their own
  "timesheets.view": STAFF,
  "timesheets.review": ADMIN_ED,  // admin/ED can approve any; supervisors are checked at the API layer

  // HC-EPIC-004 · Community Partner Portal (staff-facing).
  // The `harborwell_partner_manager` internal role receives all five;
  // `admin` receives them via the implicit admin bypass in the backend.
  "community_partner_organizations.view": ["admin", "executive_director", "harborwell_partner_manager"],
  "community_partner_organizations.create": ["admin", "harborwell_partner_manager"],
  "community_partner_organizations.approve": ["admin", "harborwell_partner_manager"],
  "community_partner_organizations.suspend": ["admin", "harborwell_partner_manager"],
  "community_partner_users.administer": ["admin", "harborwell_partner_manager"],
};

// Module visibility in sidebar
export const MODULE_VISIBILITY = {
  dashboard: STAFF,
  care_seekers: STAFF,
  intake: STAFF,
  notes: STAFF,
  referrals: STAFF,
  resources: ALL_AUTH,
  outreach: ALL_AUTH,
  maternal: ADMIN_ED,
  tasks: ALL_AUTH,
  appointments: STAFF,
  training: ALL_AUTH,
  tickets: ALL_AUTH,
  reports: ADMIN_ED,
  community_intel: ADMIN_ED,
  users: ADMIN_ED, // can view; only admin can edit
  audit: ADMIN_ONLY,
  integrations: ADMIN_ED,
  document_templates: STAFF,
  recently_deleted: ADMIN_ED,
  partner_submissions: ADMIN_ED,
  volunteers_admin: ADMIN_ED,
  field_checkins_admin: ADMIN_ED,
  timesheets: STAFF,
  inbox: STAFF,
  roles: ADMIN_ED,
  retention: ADMIN_ED,
  maintenance: ADMIN_ED,
  compliance: ADMIN_ED,
  legal_holds: ADMIN_ED,
  exceptions: ADMIN_ED,
  governance_keys: ADMIN_ED,
  release_docs: ADMIN_ED,
  security_center: ADMIN_ED,
  email_infra: ADMIN_ONLY,
  change_history: ADMIN_ED,
  operations_center: ADMIN_ED,
  executive_ops: ADMIN_ED,
  exec_notifications: ADMIN_ED,
  backup_dr: ADMIN_ONLY,
  partner_recovery: ADMIN_ONLY,  // Iter 76.19 — falls back only if perms haven't loaded
};

// Module key → permission key that controls sidebar visibility (Batch 11 RBAC).
// `useAuth().canSeeModule(moduleKey)` consults this first; if the user's
// DB-driven permission set contains the mapped key, the module is shown.
// Falls back to the static MODULE_VISIBILITY table above if the perm set
// hasn't loaded yet.
//
// Iter 77.4 update: dashboard-oriented modules now map to their
// per-dashboard permission instead of the generic `dashboard.view` /
// `reports.view`. Non-dashboard modules keep their existing mappings.
export const MODULE_PERMISSION_MAP = {
  dashboard: "dashboard.home.view",
  care_seekers: "care_seekers.view",
  intake: "intakes.view",
  notes: "notes.view",
  referrals: "referrals.view",
  resources: "resources.view",
  outreach: "outreach.view",
  maternal: "maternal.view",
  tasks: "tasks.view",
  appointments: "appointments.view",
  training: "training.view",
  tickets: "tickets.view",
  reports: "dashboard.reports.view",
  community_intel: "reports.view",
  users: "users.view",
  audit: "audit.view",
  integrations: "integrations.manage",
  document_templates: "document_templates.manage",
  recently_deleted: "deleted.restore",
  partner_submissions: "partner_submissions.manage",
  volunteers_admin: "volunteers.view",
  field_checkins_admin: "field_checkins.view_all",
  timesheets: "timesheets.view",
  inbox: "dashboard.home.view",
  roles: "roles.view",
  retention: "retention.view",
  maintenance: "maintenance.view",
  compliance: "compliance.view",
  legal_holds: "retention.view",
  exceptions: "compliance.view",
  governance_keys: "compliance.manage",
  security_center: "compliance.manage",
  email_infra: "compliance.manage",
  change_history: "audit.view",
  operations_center: "dashboard.executive_ops.view",
  executive_ops: "dashboard.executive_ops.view",
  exec_notifications: "dashboard.executive_notifications.view",
  // Iter 77.4 · dedicated module keys for dashboard-only nav items.
  exec_dashboard: "dashboard.compliance.view",       // Executive/Compliance dashboard nav item
  funder_dashboard: "dashboard.funder.view",         // Funder dashboard nav item
  // Newly gated (Iter 76.8) — every nav-visible module must have a mapped
  // permission or `canSeeModule` returns false once perms are loaded.
  partners: "compliance.manage",           // /admin/partners — governance-tier
  partner_recovery: "partners.recover",     // /admin/partners/:id/recovery — Iter 76.19
  trust_center: "compliance.manage",       // /admin/trust-center — governance-tier
  release_docs: "audit.view",              // /admin/uat — internal release docs
  backup_dr: "compliance.manage",          // /admin/backup-dr — Iter 76.13
};

export function userCan(user, permission) {
  if (!user || !user.role) return false;
  const allowed = PERMISSIONS[permission];
  if (!allowed) return false;
  return allowed.includes(user.role);
}

export function userCanSeeModule(user, moduleKey) {
  if (!user || !user.role) return false;
  const allowed = MODULE_VISIBILITY[moduleKey];
  if (!allowed) return false;
  return allowed.includes(user.role);
}

// Friendly labels for the matrix UI
export const PERMISSION_LABELS = {
  "users.view": "View team members",
  "users.create": "Add team members",
  "users.edit": "Edit team members",
  "users.delete": "Remove team members",
  "care_seekers.view": "View Care Seekers",
  "care_seekers.create": "Add Care Seekers",
  "care_seekers.edit": "Edit Care Seekers",
  "care_seekers.delete": "Delete Care Seekers",
  "intakes.create": "Start A.N.C.H.O.R. intakes",
  "intakes.view": "View intakes",
  "notes.view": "View wellness notes",
  "notes.create": "Add wellness notes",
  "notes.delete": "Delete wellness notes",
  "referrals.view": "View referrals",
  "referrals.create": "Create referrals",
  "referrals.edit": "Update referral status",
  "referrals.delete": "Delete referrals",
  "resources.view": "View resource directory",
  "resources.create": "Add resources",
  "resources.edit": "Edit resources",
  "resources.delete": "Delete resources",
  "outreach.view": "View outreach activities",
  "outreach.create": "Log outreach activities",
  "outreach.delete": "Delete outreach activities",
  "maternal.view": "View maternal visits",
  "maternal.create": "Record maternal visits",
  "tasks.view": "View tasks",
  "tasks.create": "Create tasks",
  "tasks.edit": "Update tasks",
  "tasks.delete": "Delete tasks",
  "appointments.view": "View appointments",
  "appointments.create": "Schedule appointments",
  "appointments.edit": "Reschedule appointments",
  "appointments.cancel": "Cancel appointments",
  "appointments.delete": "Delete appointments",
  "tickets.view": "View IT tickets",
  "tickets.create": "Log IT tickets",
  "tickets.edit": "Update IT tickets",
  "tickets.delete": "Delete IT tickets",
  "dashboard.view": "View dashboard (legacy)",
  "reports.view": "Access reports page",
  "exports.download": "Download CSV / PDF exports",
  "grant_reports.email_external": "Email Grant Report to external recipient",
  "audit.view": "View audit log",
  // Iter 77.4 · per-dashboard labels
  "dashboard.home.view": "View Home Dashboard",
  "dashboard.executive_ops.view": "View Executive Operations Center",
  "dashboard.executive_notifications.view": "View Executive Notification Center",
  "dashboard.compliance.view": "View Compliance Dashboard",
  "dashboard.reports.view": "View Reports Dashboard",
  "dashboard.funder.view": "View Funder Dashboard",
  // HC-EPIC-002 · P2-POST-CLOSE-MENU-001 · Partner Submissions
  "partner_submissions.manage": "Manage Partner Resource Submissions",
};

// -----------------------------------------------------------------------
// Iter 77.4 · Dashboard-Level RBAC — client-side registry.
// Kept in lock-step with backend/dashboards.py::DASHBOARDS. The client
// uses this to know which per-dashboard permission gates each route so
// menu items can be hidden without a server roundtrip.
// -----------------------------------------------------------------------
export const DASHBOARDS = [
  { key: "home",                     permission: "dashboard.home.view",
    label: "Home Dashboard",
    route: "/", legacyPermission: "dashboard.view" },
  { key: "executive_ops",            permission: "dashboard.executive_ops.view",
    label: "Executive Operations Center",
    route: "/executive", legacyPermission: "reports.view" },
  { key: "executive_notifications",  permission: "dashboard.executive_notifications.view",
    label: "Executive Notification Center",
    route: "/executive/notifications", legacyPermission: "reports.view" },
  { key: "compliance",               permission: "dashboard.compliance.view",
    label: "Compliance Dashboard",
    route: "/admin/compliance-dashboard", legacyPermission: "compliance.view" },
  { key: "reports",                  permission: "dashboard.reports.view",
    label: "Reports Dashboard",
    route: "/reports", legacyPermission: "reports.view" },
  { key: "funder",                   permission: "dashboard.funder.view",
    label: "Funder Dashboard",
    route: "/analytics/funder-dashboard", legacyPermission: "reports.view" },
];

export const DASHBOARD_KEYS = DASHBOARDS.map((d) => d.key);
export const DASHBOARD_PERMISSIONS = DASHBOARDS.map((d) => d.permission);
export const DASHBOARD_BY_KEY = Object.fromEntries(DASHBOARDS.map((d) => [d.key, d]));
